import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toastError } from '../../utils/alertHelper';
import axios from '../../utils/axiosInstance';
import { FaEye, FaWhatsapp, FaPhone } from 'react-icons/fa';

const TicketPage = () => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All Complaint');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const limit = parseInt(process.env.REACT_APP_PAGE_LIMIT || 10);

  // Status options for filter
  const statusOptions = [
    'All Complaint',
    'RAISED',
    'IN PROGRESS', 
    'RESOLVED',
    'CLOSED'
  ];

  useEffect(() => {
    fetchTickets(currentPage, searchQuery, statusFilter);
  }, [currentPage, searchQuery, statusFilter]);

  const fetchTickets = async (page = 1, search = '', status = 'All Complaint') => {
    setLoading(true);
    try {
      const statusParam = status === 'All Complaint' ? '' : status;
      const res = await axios.get(`/admin/api/tickets?page=${page}&limit=${limit}&search=${search}&status=${statusParam}`);
      const { tickets: ticketData = [], totalCount = 0 } = res.data;

      setTickets(ticketData);
      setTotalPages(Math.ceil(totalCount / limit));
    } catch (err) {
      console.error('Failed to fetch tickets:', err);
      toastError('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const handleStatusFilter = (e) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit', 
      year: 'numeric'
    }) + ' ' + date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const getStatusBadgeClass = (status) => {
    switch (status?.toUpperCase()) {
      case 'RAISED':
        return 'bg-primary';
      case 'IN PROGRESS':
        return 'bg-warning';
      case 'RESOLVED':
        return 'bg-success';
      case 'CLOSED':
        return 'bg-secondary';
      default:
        return 'bg-info';
    }
  };

  const truncateText = (text, maxLength = 50) => {
    if (!text) return 'N/A';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  return (
    <div className="container-fluid mt-2">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0">Ticket ment</h3>
        <div className="d-flex gap-3 align-items-center">
          {/* Status Filter */}
          <div className="d-flex align-items-center">
            <select
              className="form-select"
              value={statusFilter}
              onChange={handleStatusFilter}
              style={{ 
                minWidth: '150px',
                borderColor: '#C44C23',
                color: '#C44C23'
              }}
            >
              {statusOptions.map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>
          
          {/* Search */}
          <div className="d-flex align-items-center">
            <input
              type="text"
              className="form-control"
              placeholder="Ticket No..."
              value={searchQuery}
              onChange={handleSearch}
              style={{ 
                minWidth: '200px',
                borderColor: '#C44C23'
              }}
            />
            <button 
              className="btn ms-2"
              style={{ 
                backgroundColor: '#C44C23', 
                color: 'white',
                border: 'none'
              }}
              onClick={() => fetchTickets(1, searchQuery, statusFilter)}
            >
              Search
            </button>
          </div>
          
          {/* Date Range Placeholder */}
          <div className="d-flex align-items-center">
            <input 
              type="date" 
              className="form-control me-1"
              style={{ borderColor: '#C44C23' }}
            />
            <input 
              type="date" 
              className="form-control"
              style={{ borderColor: '#C44C23' }}
            />
            <button 
              className="btn ms-2"
              style={{ 
                backgroundColor: '#C44C23', 
                color: 'white',
                border: 'none'
              }}
            >
              ↓
            </button>
          </div>
        </div>
      </div>

      {/* Tickets Table */}
      <div className="card shadow-sm">
        <div className="card-body p-0">
          {loading ? (
            <div className="d-flex justify-content-center align-items-center py-5">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover mb-0" style={{ fontSize: '14px' }}>
                <thead style={{ backgroundColor: '#f8f9fa' }}>
                  <tr>
                    <th className="py-3 px-3 text-center">TICKET NO</th>
                    <th className="py-3 px-3">NAME</th>
                    <th className="py-3 px-3">TITLE</th>
                    <th className="py-3 px-3">DATE TIME</th>
                    <th className="py-3 px-3 text-center">VIEW</th>
                    <th className="py-3 px-3">DESCRIPTION</th>
                    <th className="py-3 px-3 text-center">IMAGE</th>
                    <th className="py-3 px-3 text-center">STATUS</th>
                    <th className="py-3 px-3">REMARK</th>
                    <th className="py-3 px-3">DEVICE</th>
                    <th className="py-3 px-3 text-center">ACTION</th>
                  </tr>
                </thead>
                <tbody>
                  {tickets.length > 0 ? (
                    tickets.map((ticket, idx) => (
                      <tr key={ticket._id || idx} className="border-bottom">
                        <td className="py-3 px-3 text-center">
                          <span style={{ color: '#C44C23', fontWeight: 'bold' }}>
                            #{ticket.ticketNo || `${(currentPage - 1) * limit + idx + 1}`}
                          </span>
                        </td>
                        <td className="py-3 px-3">
                          <div className="d-flex align-items-center">
                            <span style={{ color: '#C44C23' }}>
                              {ticket.user?.name || ticket.name || 'N/A'}
                            </span>
                          </div>
                        </td>
                        <td className="py-3 px-3">
                          <span style={{ color: '#C44C23' }}>
                            {ticket.title || 'N/A'}
                          </span>
                        </td>
                        <td className="py-3 px-3">
                          <div style={{ fontSize: '12px' }}>
                            <div>{formatDate(ticket.createdAt)}</div>
                          </div>
                        </td>
                        <td className="py-3 px-3 text-center">
                          <button 
                            className="btn btn-sm"
                            style={{ 
                              backgroundColor: '#C44C23', 
                              color: 'white',
                              border: 'none',
                              borderRadius: '50%',
                              width: '30px',
                              height: '30px'
                            }}
                            title="View Details"
                          >
                            <FaEye size={12} />
                          </button>
                        </td>
                        <td className="py-3 px-3" style={{ maxWidth: '250px' }}>
                          <span title={ticket.description}>
                            {truncateText(ticket.description)}
                          </span>
                          {ticket.description && ticket.description.length > 50 && (
                            <button 
                              className="btn btn-link p-0 ms-1"
                              style={{ 
                                color: '#C44C23',
                                fontSize: '12px',
                                textDecoration: 'none'
                              }}
                            >
                              ...Read More
                            </button>
                          )}
                        </td>
                        <td className="py-3 px-3 text-center">
                          {ticket.image ? (
                            <img 
                              src={ticket.image} 
                              alt="Ticket"
                              style={{ 
                                width: '30px', 
                                height: '30px',
                                borderRadius: '4px',
                                objectFit: 'cover'
                              }}
                            />
                          ) : (
                            <span className="text-muted">No Image</span>
                          )}
                        </td>
                        <td className="py-3 px-3 text-center">
                          <span 
                            className={`badge ${getStatusBadgeClass(ticket.status)}`}
                            style={{ fontSize: '11px' }}
                          >
                            ● {ticket.status || 'RAISED'}
                          </span>
                        </td>
                        <td className="py-3 px-3">
                          <span title={ticket.remark}>
                            {truncateText(ticket.remark, 30) || '-'}
                          </span>
                        </td>
                        <td className="py-3 px-3">
                          <span className="text-muted small">
                            {ticket.device || ticket.userAgent || '-'}
                          </span>
                        </td>
                        <td className="py-3 px-3 text-center">
                          <div className="d-flex gap-1 justify-content-center">
                            <button 
                              className="btn btn-sm"
                              style={{ 
                                backgroundColor: '#25D366', 
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                padding: '4px 8px'
                              }}
                              title="WhatsApp"
                            >
                              <FaWhatsapp size={12} />
                            </button>
                            <button 
                              className="btn btn-sm"
                              style={{ 
                                backgroundColor: '#007bff', 
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                padding: '4px 8px'
                              }}
                              title="Call"
                            >
                              <FaPhone size={12} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="11" className="text-center py-5 text-muted">
                        <h5>No tickets found</h5>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-center align-items-center p-3 border-top">
              <div className="d-flex gap-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    className="btn btn-sm"
                    style={{
                      backgroundColor: currentPage === page ? '#C44C23' : 'transparent',
                      color: currentPage === page ? 'white' : '#C44C23',
                      border: '1px solid #C44C23',
                      minWidth: '35px'
                    }}
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TicketPage;
